import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { LIFECYCLE_LABELS, LIFECYCLE_STATES } from '@hospasense/contracts';
import { useAuth } from '../lib/auth.js';

interface Command {
  id: string;
  label: string;
  hint: string;
  to: string;
  permission: string;
}

/**
 * Keyboard-first command palette (Ctrl/Cmd+K). Lists only destinations the
 * operator's resolved permissions actually allow.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const commands = useMemo<Command[]>(() => {
    const base: Command[] = [
      {
        id: 'overview',
        label: 'Go to Mission Overview',
        hint: 'Portfolio summary',
        to: '/',
        permission: 'overview.read',
      },
      {
        id: 'orgs',
        label: 'Go to Organisations',
        hint: 'Registry',
        to: '/organisations',
        permission: 'organisation.read',
      },
      {
        id: 'onboarding',
        label: 'Go to Onboarding',
        hint: 'Checklists and go-live',
        to: '/onboarding',
        permission: 'onboarding.read',
      },
      {
        id: 'commercial',
        label: 'Go to Commercial & Contracts',
        hint: 'Plans and agreements',
        to: '/commercial',
        permission: 'contract.read',
      },
      {
        id: 'entitlements',
        label: 'Go to Entitlements & Drift',
        hint: '4-Tier Matrix and drift queue',
        to: '/entitlements',
        permission: 'entitlement.read',
      },
      {
        id: 'billing',
        label: 'Go to B2B Billing & GST',
        hint: 'Tax invoices and settlement',
        to: '/billing',
        permission: 'billing.read',
      },
      {
        id: 'provisioning',
        label: 'Go to Provisioning Outbox',
        hint: 'Connector queue & signed commands',
        to: '/provisioning',
        permission: 'provisioning.read',
      },
      {
        id: 'fleet',
        label: 'Go to Fleet Topology',
        hint: 'Distributed edge nodes & signing keys',
        to: '/fleet',
        permission: 'fleet.read',
      },
      {
        id: 'releases',
        label: 'Go to Releases & Rollouts',
        hint: 'Version catalogue & progressive rings',
        to: '/releases',
        permission: 'release.read',
      },
      {
        id: 'reliability',
        label: 'Go to SRE & Reliability',
        hint: 'Fleet SLOs, probes & anomaly triage',
        to: '/reliability',
        permission: 'reliability.read',
      },
      {
        id: 'support',
        label: 'Go to Support',
        hint: 'Cases and communications',
        to: '/support',
        permission: 'case.read',
      },
      {
        id: 'approvals',
        label: 'Go to Approvals',
        hint: 'Two-person governance queue',
        to: '/approvals',
        permission: 'approval.read',
      },
      {
        id: 'audit',
        label: 'Go to Audit log',
        hint: 'Privileged activity',
        to: '/audit',
        permission: 'audit.read',
      },
      {
        id: 'analytics',
        label: 'Go to Product Analytics',
        hint: 'Metrics with full lineage',
        to: '/analytics',
        permission: 'analytics.read',
      },
      {
        id: 'health',
        label: 'Go to Customer Health',
        hint: 'Explainable advisory scores',
        to: '/health',
        permission: 'health.read',
      },
      {
        id: 'compliance',
        label: 'Go to Compliance Controls',
        hint: 'Four-flag control library & evidence',
        to: '/compliance',
        permission: 'compliance.read',
      },
      {
        id: 'risk',
        label: 'Go to Risk & Privacy',
        hint: 'Risks, incidents, vendors, RoPA, DPIAs',
        to: '/risk',
        permission: 'risk.read',
      },
      {
        id: 'automation',
        label: 'Go to Automation',
        hint: 'Rules, dry-run, kill switch',
        to: '/automation',
        permission: 'automation.read',
      },
      {
        id: 'reports',
        label: 'Go to Reports',
        hint: 'Immutable board & operating snapshots',
        to: '/reports',
        permission: 'report.read',
      },
      ...LIFECYCLE_STATES.map((s) => ({
        id: `orgs-${s}`,
        label: `Organisations: ${LIFECYCLE_LABELS[s]}`,
        hint: 'Filtered registry',
        to: `/organisations?lifecycleState=${s}`,
        permission: 'organisation.read',
      })),
      {
        id: 'orgs-attention',
        label: 'Organisations needing attention',
        hint: 'Stale data',
        to: '/organisations?attention=stale',
        permission: 'organisation.read',
      },
    ];
    return base.filter((c) => hasPermission(c.permission));
  }, [hasPermission]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) => `${c.label} ${c.hint}`.toLowerCase().includes(q));
  }, [commands, query]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
        setQuery('');
        setActive(0);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const run = (command: Command | undefined): void => {
    if (!command) return;
    setOpen(false);
    navigate(command.to);
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[15vh]">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-lg overflow-hidden rounded-xl border border-border bg-surface shadow-(--shadow-card)">
        <input
          ref={inputRef}
          role="combobox"
          aria-expanded="true"
          aria-controls="command-palette-list"
          aria-activedescendant={filtered[active] ? `cmd-${filtered[active].id}` : undefined}
          aria-label="Search commands"
          placeholder="Type a destination or filter…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, filtered.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              run(filtered[active]);
            }
          }}
          className="w-full border-b border-border bg-surface px-4 py-3 text-sm text-text-primary placeholder:text-text-muted focus:outline-none"
        />
        <ul
          id="command-palette-list"
          role="listbox"
          aria-label="Commands"
          className="max-h-72 overflow-y-auto py-1"
        >
          {filtered.length === 0 && (
            <li className="px-4 py-3 text-sm text-text-muted">No matching commands</li>
          )}
          {filtered.map((c, i) => (
            <li
              key={c.id}
              id={`cmd-${c.id}`}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => run(c)}
              className={`flex cursor-pointer items-center justify-between px-4 py-2 text-sm ${
                i === active ? 'bg-accent-muted text-accent-fg' : 'text-text-primary'
              }`}
            >
              <span>{c.label}</span>
              <span className="text-xs text-text-muted">{c.hint}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>,
    document.body,
  );
}
